<template>
  <v-row no-gutters>
    <v-col v-if="isFetched">
      <sidebar-row>
        <template v-if="isExtended">
          <data-input
            :placeholder="$i18n.t('sidebar.enterNoteText')"
            v-model="noteContent"
            :data-type="{ name: 'textarea' }"
            rows="4"
            class="mb-2 mt-1"
            ref="noteInput"
            background-color="rgba(236, 236, 236, 0.49)"
            outlined
            dense
            hide-details
          />
          <div class="d-flex">
            <base-button
              translation-path="button.add"
              @click="handleNoteAdd"
              :disabled="!isValid"
              :loading="isLoading"
              class="mb-5 px-8 ml-auto mr-0"
            ></base-button>
          </div>
        </template>
        <template v-else>
          <data-input
            :placeholder="$i18n.t('sidebar.enterNoteText')"
            v-model="noteContent"
            :data-type="dataType"
            class="mb-5 mt-1"
            ref="noteInput"
            background-color="rgba(236, 236, 236, 0.49)"
            outlined
            clearable
            dense
            hide-details
          >
            <template #append>
              <v-btn
                depressed
                color="primary"
                outlined
                @click="handleNoteAdd"
                block
                dark
                :loading="isLoading"
                class="addNoteBtn"
                style="padding: 0"
                :disabled="!isValid"
              >
                <v-icon style="font-size: 12px;" color="white">
                  mdi-send
                </v-icon>
              </v-btn>
            </template>
          </data-input>
        </template>
      </sidebar-row>
      <v-row no-gutters>
        <v-col v-if="notes?.length > 0">
          <v-row v-for="note in computedNotes" :key="note.id" no-gutters>
            <v-col>
              <the-note :note="note" @delete="openDeleteDialog" class="mb-6"></the-note>
            </v-col>
          </v-row>
        </v-col>
        <v-col v-else class="no-content">
          {{ $i18n.t('sidebar.noNotes') }}
        </v-col>
      </v-row>
    </v-col>
    <main-loader v-else></main-loader>
    <main-dialog
      v-if="isDialogLoaded"
      :is-visible.sync="isDialogVisible"
      :title="$i18n.t('dialog.title.deleteNote')"
      :body="$i18n.t('dialog.body.deleteNoteConfirmation')"
      @agree="handleDeleteNote"
      :loading="isConfirmButtonLoading"
      agree-text="dialog.agreeDelete"
    ></main-dialog>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';

import DataInput from '@/components/DataInput';
import TheNote from '@/components/TheNote';
import MainLoader from '@/components/MainLoader';
import SidebarRow from '@/components/SidebarRow';

export default {
  name: 'Notes',
  props: {
    featureId: {
      type: [String, Number],
      required: true,
    },
    subFeatureId: {
      type: [String, Number],
    },
    dataSourceName: {
      type: String,
      required: true,
    },
    subDataSourceName: {
      type: String,
    },
    layerId: {
      type: [String, Number],
      required: true,
    },
    isExtended: {
      type: Boolean,
      default: false,
    },
  },
  components: { SidebarRow, MainLoader, TheNote, DataInput, MainDialog: () => import('@/components/MainDialog') },
  data: () => ({
    dataType: {
      name: 'text',
    },
    noteContent: '',
    isFetched: false,
    isDialogLoaded: false,
    isDialogVisible: false,
    isConfirmButtonLoading: false,
    id: undefined,
    isLoading: false,
  }),
  computed: {
    notes: get('notes/notes@:activeDataSourceName@:activeFeatureId'),
    users: get('users/users'),
    currentUser: get('users/currentUser'),
    layerPermissions: get('users/layersPermissions@:layerId'),
    activeDataSourceName() {
      return this.subDataSourceName || this.dataSourceName;
    },
    activeFeatureId() {
      return this.subFeatureId || this.subFeatureId === 0 ? this.subFeatureId : this.featureId;
    },
    computedNotes() {
      return this.notes?.map(item => {
        return {
          ...item,
          dateAdded: this.dateText(item.added_at),
          dotsItems: this.getDotsItems(this.hasPermissions(item.owner_id)),
        };
      });
    },
    isValid() {
      return this.noteContent !== '' && this.noteContent !== null;
    },
    projectElements: get('layers/project@layers'),
    projectLayers() {
      return this.$_getFlatGroupsLayers(this.projectElements).layers;
    },
  },
  methods: {
    addNote: call('notes/addNote'),
    deleteNote: call('notes/deleteNote'),
    getNotes: call('notes/getNotes'),

    getDotsItems(value) {
      return [
        {
          name: 'delete',
          disabled: !value,
        },
      ];
    },
    hasPermissions(id) {
      return (
        this.currentUser.is_admin ||
        this.currentUser.is_superadmin ||
        id === this.currentUser.id ||
        this.layerPermissions.main_value === 2
      );
    },
    dateText(date) {
      if (date === undefined) {
        return this.$i18n.t('admin.noData');
      } else {
        return this.$dayjs(date).format('L, LTS');
      }
    },
    openDeleteDialog(id) {
      this.id = id;
      this.isDialogLoaded = true;
      this.isDialogVisible = true;
    },
    async handleNoteAdd() {
      this.isLoading = true;
      const newNote = {
        content: this.noteContent,
        target_objects: [
          {
            datasource_name: this.activeDataSourceName,
            feature_id: this.activeFeatureId,
          },
        ],
      };
      await this.addNote(newNote);
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshFormData');
      const layersIds = this.projectLayers
        .filter(layer => layer.data_source_name === this.activeDataSourceName)
        .map(layer => layer.id);
      if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
      this.noteContent = '';
      this.isLoading = false;
    },
    async handleDeleteNote() {
      try {
        this.isConfirmButtonLoading = true;
        const payload = {
          id: this.id,
          data_source_name: this.activeDataSourceName,
          feature_id: this.activeFeatureId,
        };
        await this.deleteNote(payload);
        this.$root.$emit('refreshTableData');
        this.$root.$emit('refreshFormData');
        const layersIds = this.projectLayers
          .filter(layer => layer.data_source_name === this.activeDataSourceName)
          .map(layer => layer.id);
        if (layersIds?.length) this.$root.$emit('updateLegendStylesCounts', { layersIds });
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.noteDeleted'),
        });
        this.isConfirmButtonLoading = false;
      } catch (error) {
        this.$store.set('snackbar/PUSH_ERROR!', {
          message: this.$i18n.t('errors.deleteNoteFailed'),
        });
        this.isConfirmButtonLoading = false;
      }
      this.isDialogLoaded = false;
      this.isDialogVisible = false;
    },
    async init() {
      this.isFetched = false;
      await this.getNotes({
        data_source_name: this.activeDataSourceName,
        feature_id: this.activeFeatureId,
      });
      this.isFetched = true;
    },
  },
  mounted() {
    this.$watch(
      vm => {
        const { activeDataSourceName, activeFeatureId } = vm;
        return { activeDataSourceName, activeFeatureId };
      },
      nV => {
        const { activeDataSourceName, activeFeatureId } = nV;
        if (activeDataSourceName && (activeFeatureId || activeFeatureId === 0)) this.init();
      }
    );
    this.init();
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .v-input__slot input {
    padding: 0 !important;
  }
  .v-input__slot fieldset {
    border-color: rgba(0, 0, 0, 0.08);
  }
  .v-input__append-inner {
    margin-top: 0 !important;
    align-self: center !important;
  }
}
.addNoteBtn {
  max-height: 25px;
  width: 28px;
  background-color: #1a73e8;
  ::v-deep .v-btn__loader .v-progress-circular svg {
    color: #fff;
    height: 80%;
  }
}

.addNoteBtn[disabled] {
  filter: grayscale(100%);
  ::v-deep .v-btn__loader .v-progress-circular svg {
    color: #b0b0b0 !important;
  }
}

.addNoteBtn[disabled] .v-icon {
  color: #b0b0b0 !important;
}

.no-content {
  font-size: 18px;
  line-height: 16px;
  color: #aaaaaa;
  padding: 1.5rem;
}
</style>
